/**
 * Ray-line and ray-polyline intersection functions for 2D geometry.
 * Split from intersect.ts to avoid circular dependencies with polyLine2.ts.
 * @module
 */
import { DIST_EPSILON_SQUARED, ZERO_EPSILON } from "./core";
import type { PolyLine2 } from "./polyLine2Types";
import { ray2Eval } from "./ray2";
import { vec2DistSquared, vec2Dot, vec2IsBetween, vec2IsBetweenUsingLengthBasedCalc, vec2Sub } from "./vec2";

const { abs, sqrt } = Math;

export enum IntersectionKind {
  TANGENT,
  TRANSVERSE,
}

export interface Ray2Line2Intersection {
  kind: IntersectionKind;
  t: number;
  lineT: number;

  x: number;
  y: number;
}

export function intersectLine2Ray2(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  originX: number,
  originY: number,
  dirX: number,
  dirY: number,
  tol: number = ZERO_EPSILON,
  newVecInBetween: boolean = false,
): Ray2Line2Intersection | null {
  // http://mathworld.wolfram.com/Line-LineIntersection.html

  const x3 = originX;
  const y3 = originY;

  const x4 = originX + dirX;
  const y4 = originY + dirY;

  const ax = x1 - x2;
  const ay = y1 - y2;

  const bx = x3 - x4;
  const by = y3 - y4;

  const denom = ax * by - ay * bx;

  // parallel case (TODO: use relative tolerance)
  if (abs(denom) < ZERO_EPSILON) {
    // check if line is on ray by projecting the first point onto the ray
    // and checking the squared distance

    const [pdx, pdy] = vec2Sub(x1, y1, originX, originY);
    const pt = vec2Dot(pdx, pdy, dirX, dirY);
    const [projx, projy] = ray2Eval(originX, originY, dirX, dirY, pt);

    if (vec2DistSquared(x1, y1, projx, projy) < DIST_EPSILON_SQUARED) {
      return {
        kind: IntersectionKind.TANGENT,
        t: pt,
        lineT: 0,
        x: x1,
        y: y1,
      };
    }

    // if the pt is not on the ray, these line is parallel to the ray and
    // does not practically intersect
    return null;
  }

  // crossing case
  const num0 = x1 * y2 - y1 * x2;
  const num1 = x3 * y4 - y3 * x4;
  const xnum = num0 * bx - ax * num1;
  const ynum = num0 * by - ay * num1;

  const x = xnum / denom;
  const y = ynum / denom;
  if (!newVecInBetween) {
    if (!vec2IsBetween(x1, y1, x2, y2, x, y, tol)) {
      return null;
    }
  } else {
    if (!vec2IsBetweenUsingLengthBasedCalc(x1, y1, x2, y2, x, y, tol)) {
      return null;
    }
  }

  // ray param
  const dx = x - originX;
  const dy = y - originY;

  const kind = IntersectionKind.TRANSVERSE;
  const t = vec2Dot(dx, dy, dirX, dirY);

  // normalized line param
  const dx2 = x - x1;
  const dy2 = y - y1;

  const lineLen = sqrt(ax * ax + ay * ay);

  const lineDirX = -ax / lineLen;
  const lineDirY = -ay / lineLen;

  const lineT = vec2Dot(dx2, dy2, lineDirX, lineDirY);

  return { kind, t, lineT, x, y };
}

export function ray2Line2IntersectionRayParamCompare(a: Ray2Line2Intersection, b: Ray2Line2Intersection) {
  return a.t > b.t ? 1 : -1;
}

export interface Ray2PolyLine2Intersection extends Ray2Line2Intersection {
  edgeIndex: number;
}

export function intersectRay2PolyLine2(
  pl: PolyLine2,
  originX: number,
  originY: number,
  dirX: number,
  dirY: number,
  tol: number = ZERO_EPSILON,
): Ray2PolyLine2Intersection[] {
  const ints: Ray2PolyLine2Intersection[] = [];

  for (let j = 0, lj = pl.length - 2; j < lj; j += 2) {
    const x0 = pl[j];
    const y0 = pl[j + 1];
    const x1 = pl[j + 2];
    const y1 = pl[j + 3];

    const res = intersectLine2Ray2(x0, y0, x1, y1, originX, originY, dirX, dirY, tol) as Ray2PolyLine2Intersection;

    if (res) {
      res.edgeIndex = j / 2;
      ints.push(res);
    }
  }

  if (ints.length < 2) {
    return ints;
  }

  // sort intersections

  ints.sort(ray2Line2IntersectionRayParamCompare);

  return ints;
}
